/**
 * `W4-T06` §3.6 — new messages arrive by polling.
 *
 * **The loader is what polls, not the component.** Nothing in a component fetches (ADR-011 R3), so
 * this re-runs the route's loaders on an interval; the thread loader reads its key with
 * `staleTime: 0` and everything else on the page is served from the cache it already filled. One
 * data path, and the sent message and the polled one arrive through it identically.
 *
 * Skipped while the tab is hidden and while the router is already busy — a poll that queued behind
 * a submission would only repeat the revalidation the submission triggers.
 */
import { useEffect, useRef } from 'react';
import { useNavigation, useRevalidator } from 'react-router';

export const POLL_MS = 5000;

export function usePoll(ms: number = POLL_MS): void {
  const revalidator = useRevalidator();
  const navigation = useNavigation();

  // Refs, so the interval is set up once rather than torn down on every state change.
  const busy = useRef(false);
  busy.current = revalidator.state !== 'idle' || navigation.state !== 'idle';
  const revalidate = useRef(revalidator.revalidate);
  revalidate.current = revalidator.revalidate;

  useEffect(() => {
    const timer = setInterval(() => {
      if (busy.current || document.visibilityState !== 'visible') return;
      void revalidate.current();
    }, ms);
    return () => clearInterval(timer);
  }, [ms]);
}
